"use client";

import Link from "next/link";
import useSWR from "swr";
import { useTranslations } from "next-intl";
import { Check, CircleAlert, CircleMinus, MapPin } from "lucide-react";
import { fetchCarOfferAction, type CarOffer } from "@/app/actions/car-offer";
import { cn } from "@/lib/utils";

/**
 * 렌터카 행을 펼친 영역(데스크톱)·바텀 시트 본문(모바일) — 조건과 1일가·[선택].
 * 흰 카드 없이 바깥 회색 면 위에 바로 놓는다(10-13 v13). 모바일 시트 하단은 1일 요금 +
 * 검색 날짜의 총 요금(10-14). 유료 옵션 안내 문구는 두지 않는다(옵션은 예약 단계에서 고른다).
 *
 * 조건 줄은 상품 상세의 안내 문장 원문이다: 인수 안내(핀) · 포함 사항(초록 체크) ·
 * 보증금 없음(초록 체크) · 불포함 사항(회색) · 필수 안내사항(회색 느낌표).
 * 가격·[선택]은 목록이 이미 가진 표시가를 받는다(여기서 다시 계산하지 않는다).
 */
export function CarOfferPanel({
  productId,
  locale,
  layout,
  priceLabel,
  totalLabel = null,
  href,
}: {
  productId: string;
  locale: string;
  layout: "desktop" | "mobile";
  /** "¥14,400" — 1일 표시가(마크업·환산 반영) */
  priceLabel: string | null;
  /** "총 KRW 57,600 · 4일" — 모바일 시트만, 날짜를 안 골랐으면 null */
  totalLabel?: string | null;
  href: string;
}) {
  const t = useTranslations("search.car");
  const { data, error, isLoading } = useSWR(
    ["car-offer", productId, locale],
    () => fetchCarOfferAction(productId, locale),
    { revalidateOnFocus: false, dedupingInterval: 10 * 60_000 },
  );

  const buy = (
    <div
      className={cn(
        "flex tabular-nums",
        layout === "desktop"
          ? "flex-col items-end justify-end gap-0.5 text-right"
          : "items-end justify-between border-t border-gray-200 pt-2.5",
      )}
    >
      <div className="min-w-0">
        {priceLabel ? (
          <div className="whitespace-nowrap text-lg font-bold text-gray-900">
            {priceLabel}
            <span className="ml-0.5 text-xs font-medium text-gray-500">{t("perDay")}</span>
          </div>
        ) : null}
        {layout === "mobile" && totalLabel ? (
          <div className="mt-0.5 whitespace-nowrap text-[13px] text-gray-700">{totalLabel}</div>
        ) : null}
      </div>
      <Link
        href={href}
        className={cn(
          "inline-flex items-center justify-center rounded-[10px] bg-orange-500 font-semibold text-white transition hover:bg-orange-600",
          // 데스크톱·모바일 같은 크기(10-14) — 높이 44 · 최소 폭 132 · 15px
          "h-11 min-w-33 shrink-0 px-6 text-[15px]",
          layout === "desktop" && "mt-2",
        )}
      >
        {t("select")}
      </Link>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2.5 py-1">
        {[72, 56, 64, 48].map((w) => (
          <div key={w} className="h-4 animate-pulse rounded bg-gray-200" style={{ width: `${w}%` }} />
        ))}
      </div>
    );
  }

  const conditions = data ? <Conditions offer={data} t={t} /> : (
    <p className="text-[13px] text-gray-500">{error || data === null ? t("loadFailed") : null}</p>
  );

  const colors =
    data && data.variants.length > 1 ? (
      <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-gray-500">
        <span className="mr-1">{t("colorLabel")}</span>
        {data.variants.map((name) => (
          <span
            key={name}
            className="rounded-full border border-gray-300 bg-white px-3 py-1 text-[13px] font-medium text-gray-700"
          >
            {name}
          </span>
        ))}
      </div>
    ) : null;

  if (layout === "mobile") {
    return (
      <div className="flex flex-col gap-2.5">
        {colors}
        <div className="flex flex-col gap-2.5 px-1">
          {conditions}
          {buy}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {colors}
      <div className="grid grid-cols-[minmax(0,1fr)_170px] gap-4">
        <div>{conditions}</div>
        {buy}
      </div>
    </div>
  );
}

type Line = { kind: "pin" | "ok" | "info" | "warn"; text: string };

function Conditions({
  offer,
  t,
}: {
  offer: CarOffer;
  t: ReturnType<typeof useTranslations<"search.car">>;
}) {
  const lines: Line[] = [
    ...(offer.pickupNote ? [{ kind: "pin" as const, text: offer.pickupNote }] : []),
    ...offer.inclusions.map((text) => ({ kind: "ok" as const, text })),
    ...(offer.depositFree ? [{ kind: "ok" as const, text: t("depositFree") }] : []),
    ...offer.exclusions.map((text) => ({ kind: "info" as const, text })),
    ...offer.requirements.map((text) => ({ kind: "warn" as const, text })),
  ];
  if (lines.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1.5 text-[13px] leading-[1.45] text-gray-700">
      {lines.map((line, index) => {
        const Icon =
          line.kind === "pin" ? MapPin : line.kind === "ok" ? Check : line.kind === "info" ? CircleMinus : CircleAlert;
        return (
          <li
            key={`${line.kind}-${index}`}
            className={cn("flex items-start gap-1.5", line.kind === "ok" && "text-green-600")}
          >
            <Icon
              aria-hidden
              strokeWidth={line.kind === "ok" ? 2.25 : 1.8}
              className={cn(
                "mt-0.5 h-3.75 w-3.75 shrink-0",
                line.kind === "ok" ? "text-green-600" : "text-gray-500",
              )}
            />
            <span className="min-w-0">{line.text}</span>
          </li>
        );
      })}
    </ul>
  );
}
